import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import { observer } from 'mobx-react-lite'

import { prepareRealtyTypeFilterOptions } from 'entities/kyrgyzstan'
import { kyrgyzstanRealtyTypesStore } from 'entities/kyrgyzstan/model'

import { RealtyType } from 'shared/api/kyrgyzstan/search'
import { CheckBoxSimple } from 'shared/ui/check-box-simple'
import { OptionsList } from 'shared/ui/options-list'
import { SimpleLoader } from 'shared/ui/simple-loader'

import s from './styles.module.scss'

interface RealtyTypeOptionsProps {
  selectedTypeIds?: number[]
  onTypeChange: (typeIds?: number[]) => void
}

export const RealtyTypeOptions = observer(
  ({ selectedTypeIds = [], onTypeChange }: RealtyTypeOptionsProps) => {
    const { t } = useTranslation()

    const {
      realtyTypes,
      isLoading: isLoadingTypes,
      error: typesError,
      fetchRealtyTypes,
    } = kyrgyzstanRealtyTypesStore

    useEffect(() => {
      void fetchRealtyTypes({
        has_object: true,
      })
    }, [fetchRealtyTypes])

    const options = prepareRealtyTypeFilterOptions(realtyTypes, t('any'))

    const handleOptionClick = (option: RealtyType) => {
      if (option.id === 0) {
        onTypeChange(undefined)
        return
      }

      if (selectedTypeIds.includes(option.id)) {
        const newTypeIds = selectedTypeIds.filter((id) => id !== option.id)
        onTypeChange(newTypeIds.length > 0 ? newTypeIds : undefined)
      } else {
        onTypeChange([...selectedTypeIds, option.id])
      }
    }

    const getIsChecked = (option: RealtyType): boolean => {
      if (option.id === 0) {
        return selectedTypeIds.length === 0
      }
      return selectedTypeIds.includes(option.id)
    }

    if (isLoadingTypes) {
      return (
        <div className={s.loader}>
          <SimpleLoader />
        </div>
      )
    }

    if (typesError) {
      return <div className={s.error}>{typesError}</div>
    }

    return (
      <OptionsList<RealtyType>
        options={options}
        renderContent={(option) => (
          <span className="medium-16">{option.name}</span>
        )}
        renderControl={(option) => (
          <CheckBoxSimple
            isChecked={getIsChecked(option)}
            isBigSize
          />
        )}
        onOptionClick={handleOptionClick}
      />
    )
  },
)
